import React, { Suspense } from 'react';

import { PillBadge } from '@lightspeed/flame/Badge';
import { Bone } from '@lightspeed/flame/Bone';
import { Button } from '@lightspeed/flame/Button';
import { Box, FlameTheme, Flex } from '@lightspeed/flame/Core';
import { DropdownContent } from '@lightspeed/flame/Dropdown';
import { FormHelper } from '@lightspeed/flame/FormField';
import { IconLockClosed } from '@lightspeed/flame/Icon/LockClosed';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@lightspeed/flame/Modal';
import { Text, TextLink } from '@lightspeed/flame/Text';
import { Tooltip } from '@lightspeed/flame/Tooltip';
import { useQuery } from '@tanstack/react-query';

import { createReactRenderer } from 'helpers/react-renderer';
import { hasFeature } from 'root/api/admin/features';
import { fetchPaymentsGatewayName } from 'root/api/helpers';
import getCardBrandIcon from 'root/apps/app/src/payments/CardBrandIcons';
import Autocomplete from 'root/apps/Autocomplete';
import { trackAnalyticsEvent } from 'root/apps/helpers/AnalyticsTracking';
import { translate } from 'root/apps/pages/utils/translate';
import { fetchSavedCards, PAYMENT_METHOD_TYPES } from 'root/apps/payments/SavedCardsAPI';

import OptInModal from './OptInModal';
import {
  AddCardBox,
  AddCardBoxDisabled,
  AddCardText,
  AddCardTextDisabled,
  CardListingText,
  ListingFlex,
  OverlayDiv,
  StyledDropdown,
  StyledDropdownText,
} from './SavedCards.styles';
import { FormattedAPISavedCard, SavedCardsProps } from './SavedCards.types';
import {
  fetchNicknames,
  NicknameValidationError,
  removeCard,
  removeNickname,
  setAsDefault,
  updateNickname,
} from './services/savedCardsService';

const AddSavedCardModal = React.lazy(() => import('../AddSavedCardModal'));

// IMPORTANT: This regex must be kept in sync with the nickname validation in lsp-processing-service.
// Current rules: 1-20 chars, letters (including accented/diacritics), spaces, apostrophes, hyphens,
// &, #, ^. No leading/trailing spaces, no consecutive special chars, must start/end with a letter.
// Numbers are NOT allowed.
const NICKNAME_PATTERN = /^[\p{L}](?:[\p{L} '\-&#^]*[\p{L}])?$/u;
const NICKNAME_MAX_LENGTH = 20;
const CONSECUTIVE_SPECIAL_PATTERN = /['\-&#^ ]{2}/;

const validateNickname = (value: string): string | null => {
  if (value.length === 0) return null;
  if (value.length > NICKNAME_MAX_LENGTH)
    return translate(`Enter ${NICKNAME_MAX_LENGTH} characters or fewer.`);
  if (value !== value.trim()) return translate('Remove spaces from the beginning or end.');
  if (CONSECUTIVE_SPECIAL_PATTERN.test(value))
    return translate('Remove consecutive special characters.');
  if (/\d/.test(value)) return translate('Use letters only. No numbers.');
  if (!NICKNAME_PATTERN.test(value))
    return translate('Use only letters, spaces, apostrophes, hyphens, &, # or ^.');
  return null;
};

const SavedCards: React.FC<SavedCardsProps> = ({ customerRef, customerOptIn }) => {
  const [currentCustomerOptIn, setCurrentCustomerOptIn] = React.useState<boolean | null>(
    customerOptIn
  );
  const [customerJustOptedIn, setCustomerJustOptedIn] = React.useState<boolean>(false);
  const hasAddCardRight = window.merchantos.session.hasRight('customers_add_card');
  const hasEditCardRight = window.merchantos.session.hasRight('customers_edit_card');
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isOptInModalOpen, setIsOptInModalOpen] = React.useState(false);
  const [removeModalIsOpen, setRemoveModalIsOpen] = React.useState(false);
  const savedCardRef = React.useRef<string | null>(null);
  const [removeInProgress, setremoveInProgress] = React.useState(false);
  const [nicknameModalIsOpen, setNicknameModalIsOpen] = React.useState(false);
  const [nicknameCardRef, setNicknameCardRef] = React.useState<string | null>(null);
  const [nicknameValue, setNicknameValue] = React.useState<string | null>(null);
  const [nicknameSaveInProgress, setNicknameSaveInProgress] = React.useState(false);
  const [nicknameError, setNicknameError] = React.useState<string | null>(null);
  const [removeNicknameModalIsOpen, setRemoveNicknameModalIsOpen] = React.useState(false);
  const [removeNicknameCardRef, setRemoveNicknameCardRef] = React.useState<string | null>(null);
  const [removeNicknameInProgress, setRemoveNicknameInProgress] = React.useState(false);
  const [hasNicknameFeature, setHasNicknameFeature] = React.useState(false);
  const [nicknameOptions, setNicknameOptions] = React.useState<{ value: string; label: string }[]>(
    []
  );

  React.useEffect(() => {
    hasFeature('CustomerCardOnFileNickname').then(setHasNicknameFeature);
  }, []);

  React.useEffect(() => {
    if (nicknameModalIsOpen) {
      fetchNicknames().then((nicknames) => {
        setNicknameOptions(nicknames.map((n) => ({ value: n, label: n })));
      });
    } else {
      setNicknameOptions([]);
    }
  }, [nicknameModalIsOpen]);

  // Accessing the payment provider to check if the gateway is Lightspeed Payments
  const gatewayNameQuery = useQuery({
    queryKey: ['gatewayName'],
    queryFn: () => {
      const accountId = window.merchantos.session.getSystemCustomerID();
      const shop = window.merchantos.session.getShop();
      if (!accountId || !shop || !shop.shopID) {
        return '';
      }
      return fetchPaymentsGatewayName({
        accountID: accountId.toString(),
        shopID: shop.shopID.toString(),
      });
    },
    retry: false,
    cacheTime: 300000, // 5 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const isGatewayLightspeedPayments = gatewayNameQuery?.data === 'lightspeed-payments';
  const LSPD_PAYMENTS_URL = '/payments/lightspeed-payments/merchant-portal';

  const handleOptInClick = () => {
    setIsOptInModalOpen(true);
  };

  const navigateToLSPaymentsSignup = () => (event: React.MouseEvent) => {
    event.preventDefault();
    trackAnalyticsEvent({
      eventCategory: 'cof',
      eventName: 'paywall',
      eventAction: 'link_click',
    });
    window.PageRouter.goTo(LSPD_PAYMENTS_URL);
  };

  const {
    data: savedCardsList,
    isFetching,
    isError,
    refetch,
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useQuery<FormattedAPISavedCard[]>({
    queryKey: ['savedCards', customerRef],
    queryFn: () =>
      fetchSavedCards(customerRef, [
        PAYMENT_METHOD_TYPES.CARD,
        PAYMENT_METHOD_TYPES.US_BANK_ACCOUNT,
      ]),
    enabled: !!customerRef && isGatewayLightspeedPayments, // only fetch saved cards if the customer exists and the gateway is Lightspeed Payments
    retry: false,
    cacheTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  if (!isGatewayLightspeedPayments) {
    return (
      <FlameTheme>
        <ListingFlex>
          <Text>
            <TextLink href={LSPD_PAYMENTS_URL} onClick={navigateToLSPaymentsSignup()}>
              Sign up
            </TextLink>{' '}
            to Lightspeed Payments to use this feature.
          </Text>
        </ListingFlex>
      </FlameTheme>
    );
  }

  if (!currentCustomerOptIn) {
    return (
      <FlameTheme>
        <Box style={{ position: 'relative' }} data-test="feature-wall-box">
          <AddCardBox>
            <AddCardText>+ Add</AddCardText>
          </AddCardBox>
          <OverlayDiv>
            <Box>
              <IconLockClosed color="white" />
              <TextLink
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleOptInClick();
                }}
              >
                Unlock Saved Payment Method
              </TextLink>
            </Box>
          </OverlayDiv>
          <OptInModal
            isOpen={isOptInModalOpen}
            onRequestClose={() => {
              setIsOptInModalOpen(false);
              if (customerJustOptedIn) {
                setCurrentCustomerOptIn(true);
              }
            }}
            onComplete={() => {
              setCustomerJustOptedIn(true);
            }}
            swapStepTwoButtonOrder={false}
          />
        </Box>
      </FlameTheme>
    );
  }

  // Ensure customerRef is defined
  if (!customerRef) {
    return (
      <FlameTheme>
        <ListingFlex>
          <Text color="#777777">Save the customer before adding a saved card.</Text>
        </ListingFlex>
      </FlameTheme>
    );
  }

  if (isError) {
    return (
      <FlameTheme>
        <ListingFlex>
          <Text>Error fetching saved cards.</Text>
        </ListingFlex>
      </FlameTheme>
    );
  }

  const ExpiredMsg = () => (
    <Text color="#DE2E37" fontSize="12px">
      Expired
    </Text>
  );

  const handleSetAsDefault = async (remoteCardRef: string) => {
    try {
      await setAsDefault(customerRef, remoteCardRef);
      refetch();
    } catch (error) {
      // Error handling is already done in the service
    }
  };

  const handleRemoveCard = async (remoteCardRef: string) => {
    setremoveInProgress(true);
    try {
      await removeCard(customerRef, remoteCardRef);
      trackAnalyticsEvent({
        eventCategory: 'cof',
        eventName: 'rm',
        eventAction: 'rm_via_customer_panel',
        eventLabel: 'success',
      });
      refetch();
    } catch (error) {
      trackAnalyticsEvent({
        eventCategory: 'cof',
        eventName: 'rf',
        eventAction: 'rm_via_customer_panel',
        eventLabel: 'error',
      });
      // Error handling is already done in the service
    } finally {
      setremoveInProgress(false);
    }
    savedCardRef.current = null;
  };

  const handleSaveNickname = async () => {
    if (!nicknameCardRef || !customerRef) return;

    if (nicknameValue) {
      const clientError = validateNickname(nicknameValue);
      if (clientError) {
        setNicknameError(clientError);
        return;
      }
    }

    setNicknameSaveInProgress(true);
    try {
      await updateNickname(customerRef, nicknameCardRef, nicknameValue);
      refetch();
      closeNicknameModal();
    } catch (error) {
      if (error instanceof NicknameValidationError) {
        setNicknameError(error.message);
      }
      // Non-validation errors are already handled in the service via window.alertUser()
    } finally {
      setNicknameSaveInProgress(false);
    }
  };

  const closeNicknameModal = () => {
    setNicknameModalIsOpen(false);
    setNicknameCardRef(null);
    setNicknameValue(null);
    setNicknameError(null);
  };

  const handleRemoveNickname = async () => {
    if (!removeNicknameCardRef || !customerRef) return;
    setRemoveNicknameInProgress(true);
    try {
      await removeNickname(customerRef, removeNicknameCardRef);
      refetch();
    } catch {
      // removeNickname() already shows an alert to the user via window.alertUser
    } finally {
      setRemoveNicknameInProgress(false);
      setRemoveNicknameModalIsOpen(false);
      setRemoveNicknameCardRef(null);
    }
  };

  const handleAddNewCardComplete = () => {
    setIsModalOpen(false);
    refetch();
  };

  return (
    <FlameTheme>
      {isFetching ? (
        <ListingFlex data-test="loading-skeleton">
          <Box>
            <Flex alignItems="center">
              <Box width="24px">
                <Bone height={4} />
              </Box>
              <Box width="100px" ml="12px">
                <Bone height={4} />
              </Box>
            </Flex>
          </Box>
          <Box width="50px" ml="12px">
            <Bone height={4} />
          </Box>
        </ListingFlex>
      ) : (
        <>
          {savedCardsList?.length === 0 && (
            <ListingFlex>
              <Text>No saved cards found.</Text>
            </ListingFlex>
          )}
          {savedCardsList?.map((card: FormattedAPISavedCard) => {
            const isACH = card.type === PAYMENT_METHOD_TYPES.US_BANK_ACCOUNT;
            const displayName = isACH ? translate(`${card.accountType} account`) : card.brand;
            const Icon = getCardBrandIcon(
              isACH ? PAYMENT_METHOD_TYPES.US_BANK_ACCOUNT : card.brand!
            );

            return (
              <ListingFlex key={card.remoteCardRef}>
                <Box>
                  <Flex alignItems="center">
                    <Icon size="24px" />
                    <Box ml="12px">
                      <CardListingText>
                        <var>{displayName}</var> •••• <var>{card.last4}</var>
                      </CardListingText>
                      {card.isExpired && <ExpiredMsg />}
                      {hasNicknameFeature && card.nickname && (
                        <Text fontSize="12px" color="#777777">
                          {card.nickname}
                        </Text>
                      )}
                    </Box>
                  </Flex>
                </Box>
                <Box>
                  {card.isDefaultSelected && (
                    <PillBadge>
                      <Text textTransform="none" fontSize="14px" letterSpacing="0px">
                        Default
                      </Text>
                    </PillBadge>
                  )}
                  {hasEditCardRight && (
                    <StyledDropdown
                      data-test="action-dropdown"
                      buttonContent=""
                      width="fit-content"
                      placement="bottom-end"
                    >
                      {/* Only show "Set as Default" for cards, not ACH */}
                      {!isACH && !card.isDefaultSelected && (
                        <DropdownContent>
                          {card.isExpired ? (
                            <StyledDropdownText
                              data-test="expired-set-as-default-action"
                              color="#777"
                              style={{ fontStyle: 'italic' }}
                              onClick={() =>
                                window.alertUser('This Card is expired. Unable to set as default.')
                              }
                            >
                              Set as Default
                            </StyledDropdownText>
                          ) : (
                            <StyledDropdownText
                              data-test="set-as-default-action"
                              color="#333"
                              onClick={() => {
                                handleSetAsDefault(card.remoteCardRef);
                              }}
                            >
                              Set as Default
                            </StyledDropdownText>
                          )}
                        </DropdownContent>
                      )}
                      {hasNicknameFeature && (
                        <DropdownContent>
                          {card.nickname ? (
                            <StyledDropdownText
                              color="#333"
                              onClick={() => {
                                setRemoveNicknameCardRef(card.remoteCardRef);
                                setRemoveNicknameModalIsOpen(true);
                              }}
                            >
                              Remove nickname
                            </StyledDropdownText>
                          ) : (
                            <StyledDropdownText
                              color="#333"
                              onClick={() => {
                                setNicknameCardRef(card.remoteCardRef);
                                setNicknameValue(card.nickname ?? null);
                                setNicknameModalIsOpen(true);
                              }}
                            >
                              Add nickname
                            </StyledDropdownText>
                          )}
                        </DropdownContent>
                      )}
                      <DropdownContent>
                        <StyledDropdownText
                          color="#DE2E37"
                          onClick={() => {
                            setRemoveModalIsOpen(true);
                            savedCardRef.current = card.remoteCardRef;
                          }}
                        >
                          Delete
                        </StyledDropdownText>
                      </DropdownContent>
                    </StyledDropdown>
                  )}
                </Box>
              </ListingFlex>
            );
          })}
          {hasAddCardRight &&
            savedCardsList &&
            (savedCardsList?.length < 5 ? (
              <AddCardBox>
                <AddCardText onClick={() => setIsModalOpen(true)}>+ Add</AddCardText>
              </AddCardBox>
            ) : (
              <AddCardBoxDisabled>
                <Tooltip
                  light={true}
                  placement="auto-start"
                  // @ts-ignore
                  content={
                    <Text textAlign="center">
                      To add a payment method,
                      <br />
                      delete a saved payment method
                    </Text>
                  }
                >
                  <AddCardTextDisabled>+ Add</AddCardTextDisabled>
                </Tooltip>
              </AddCardBoxDisabled>
            ))}
          {isModalOpen && (
            <Suspense fallback={<></>}>
              <AddSavedCardModal
                customerId={customerRef!}
                onRequestClose={() => setIsModalOpen(false)}
                onComplete={() => handleAddNewCardComplete()}
              />
            </Suspense>
          )}
        </>
      )}
      <Modal
        isOpen={nicknameModalIsOpen}
        data-test="add-nickname-modal"
        onRequestClose={closeNicknameModal}
      >
        <ModalHeader onCloseClicked={closeNicknameModal}>Add nickname</ModalHeader>
        <ModalBody>
          <Text mb="8px">Give this card a nickname to help customers recognize it later.</Text>
          <Autocomplete
            isCreatable
            isClearable
            placeholder="Enter a nickname"
            value={nicknameValue ? { value: nicknameValue, label: nicknameValue } : null}
            options={nicknameOptions}
            onChange={(selected: { value: string; label: string } | null) => {
              setNicknameValue(selected?.value || null);
              setNicknameError(null);
            }}
            onCreateOption={(inputValue: string) => {
              setNicknameValue(inputValue);
              setNicknameError(null);
            }}
            hasError={!!nicknameError}
            data-test="nickname-autocomplete"
          />
          {nicknameError ? (
            <FormHelper mt="8px" color="danger">
              {translate(nicknameError)}
            </FormHelper>
          ) : (
            <FormHelper mt="8px" color="gray-600">
              {translate(
                'Use up to 20 letters, spaces, hyphens, apostrophes, &, # or *. No numbers.'
              )}
            </FormHelper>
          )}
        </ModalBody>
        <ModalFooter>
          <Flex justifyContent="right">
            <Box mr="8px">
              <Button onClick={closeNicknameModal}>Cancel</Button>
            </Box>
            <Button
              variant="secondary"
              fill={true}
              disabled={!nicknameValue || !!nicknameError}
              loading={nicknameSaveInProgress}
              data-test="confirm-save-nickname"
              onClick={handleSaveNickname}
            >
              Add nickname
            </Button>
          </Flex>
        </ModalFooter>
      </Modal>
      <Modal
        isOpen={removeNicknameModalIsOpen}
        data-test="remove-nickname-modal"
        onRequestClose={() => setRemoveNicknameModalIsOpen(false)}
      >
        <ModalHeader onCloseClicked={() => setRemoveNicknameModalIsOpen(false)}>
          Remove nickname
        </ModalHeader>
        <ModalBody>
          <Text>
            {translate(
              "You're about to remove the nickname from this card. You can add a new one anytime."
            )}
          </Text>
        </ModalBody>
        <ModalFooter>
          <Flex justifyContent="right">
            <Box mr="8px">
              <Button onClick={() => setRemoveNicknameModalIsOpen(false)}>Cancel</Button>
            </Box>
            <Button
              variant="danger"
              fill={true}
              loading={removeNicknameInProgress}
              data-test="confirm-remove-nickname"
              onClick={handleRemoveNickname}
            >
              Remove nickname
            </Button>
          </Flex>
        </ModalFooter>
      </Modal>
      <Modal isOpen={removeModalIsOpen} data-test="remove-card-modal">
        <ModalHeader onCloseClicked={() => setRemoveModalIsOpen(false)}>
          Delete a saved payment method attached to this profile
        </ModalHeader>
        <ModalBody>
          <Text>Are you sure you want to delete this saved payment method?</Text>
        </ModalBody>
        <ModalFooter>
          <Flex justifyContent="right">
            <Box mr="8px">
              <Button onClick={() => setRemoveModalIsOpen(false)}>Cancel</Button>
            </Box>
            <Button
              loading={removeInProgress}
              variant="danger"
              data-test="confirm-remove-card"
              fill={true}
              onClick={() => {
                if (savedCardRef.current) {
                  handleRemoveCard(savedCardRef.current);
                }
                setRemoveModalIsOpen(false);
              }}
            >
              Delete saved payment method
            </Button>
          </Flex>
        </ModalFooter>
      </Modal>
    </FlameTheme>
  );
};

const RenderedComponent = createReactRenderer({
  component: SavedCards,
  selector: '[data-react-root="SavedCards"]',
});

export default SavedCards;

history.onChangeState(
  {
    name: 'customer.views.customer',
    form_name: 'view',
    tab: ['details'],
  },
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);
